import { withPermissions } from '@monorepo/shared/hooks/permissions/withPermissions';
import { CalendarEventStatus, isCalendarEventStarted } from 'mapistry-shared';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { fetchInspectionCalendarStencils } from '../../../../actions/calendar';
import APP from '../../../../config';
import { FormCalendar } from '../../../propTypes';
import withProvider from '../../../withProvider';
import BaseInspectionCalendarCard from '../BaseInspectionCalendarCard';

// Statuses whose tooltip copy prompts creating an inspection rather than
// describing an existing one, so there is no assignee to report.
const STATUSES_WITHOUT_ASSIGNEES = [
  CalendarEventStatus.AS_NEEDED,
  CalendarEventStatus.NOT_NEEDED,
  CalendarEventStatus.EXTRA,
];

class FixedFrequencyInspectionCalendarCard extends Component {
  constructor(props) {
    super(props);
    this.tooltipTitle = this.tooltipTitle.bind(this);
  }

  componentDidMount() {
    const { calendar, fetchStencils, projectId } = this.props;
    fetchStencils(projectId, calendar.name);
  }

  assigneeText(status, events) {
    if (STATUSES_WITHOUT_ASSIGNEES.includes(status)) {
      return null;
    }
    const names = [
      ...new Set(
        events.flatMap((event) =>
          (event.assignees || [])
            .map((assignee) => assignee.name)
            .filter(Boolean),
        ),
      ),
    ];
    return `Assigned to ${names.length ? names.join(', ') : 'no one'}`;
  }

  tooltipText(status, count, events) {
    const isSingle = count === 1;
    const isStarted = isCalendarEventStarted(events[0]);
    const startedText = isSingle && isStarted ? ' (started)' : '';

    switch (status) {
      case CalendarEventStatus.COMPLETE:
        return isSingle
          ? '1 Completed inspection'
          : `${count} Completed inspections`;
      case CalendarEventStatus.EXCEPTION:
        return isSingle
          ? '1 Exception recorded'
          : `${count} Exceptions recorded`;
      case CalendarEventStatus.OVERDUE:
        return isSingle
          ? `1 Missed inspection${startedText}`
          : `${count} Missed inspections`;
      case CalendarEventStatus.UPCOMING:
        return isSingle
          ? `1 Inspection due soon${startedText}`
          : `${count} Inspections due soon`;
      case CalendarEventStatus.AS_NEEDED:
        return 'Create an inspection';
      case CalendarEventStatus.NOT_NEEDED:
        return 'Not Required';
      case CalendarEventStatus.EXTRA:
        return isStarted ? '1 Inspection (started)' : 'Create a new inspection';
      default:
        if (events[0].dueDate) {
          return count === 1
            ? `1 Future inspection${startedText}`
            : `${count} Future inspections`;
        }
        return count === 1
          ? '1 Inspection started but not completed'
          : `${count} Inspections started but not completed`;
    }
  }

  tooltipTitle(events, tooMuchData) {
    if (tooMuchData) {
      return 'More Details';
    }
    const statusCount = events.reduce((acc, event) => {
      const existingStatus = acc[event.status];
      if (existingStatus) {
        acc[event.status] = existingStatus + 1;
      } else {
        acc[event.status] = 1;
      }
      return acc;
    }, {});
    const statusCountKeys = Object.keys(statusCount);
    const lines = statusCountKeys
      .filter(
        (status) =>
          !(
            status === CalendarEventStatus.AS_NEEDED &&
            statusCountKeys.length > 1
          ),
      )
      .map((status) => {
        const eventsForStatus = events.filter(
          (event) => event.status === status,
        );
        return {
          status,
          statusText: this.tooltipText(
            status,
            statusCount[status],
            eventsForStatus,
          ),
          assigneeText: this.assigneeText(status, eventsForStatus),
        };
      });

    // Report a single assignee line when every status shares the same
    // assignees, rather than repeating it under each one.
    const assigneeTexts = [
      ...new Set(lines.map((line) => line.assigneeText).filter(Boolean)),
    ];
    const sharedAssigneeText =
      assigneeTexts.length === 1 ? assigneeTexts[0] : '';

    return (
      <>
        {lines.map(({ status, statusText, assigneeText }) => (
          <div key={status}>
            <div>{statusText}</div>
            {!sharedAssigneeText && assigneeText && <div>{assigneeText}</div>}
          </div>
        ))}
        {sharedAssigneeText && <div>{sharedAssigneeText}</div>}
      </>
    );
  }

  render() {
    const {
      calendar,
      organizationId,
      hasProjectSettingsPermission,
      inspectionStencils,
    } = this.props;

    return (
      <BaseInspectionCalendarCard
        calendar={calendar}
        enableFrequencySetupPrompt
        organizationId={organizationId}
        tooltipTitle={this.tooltipTitle}
        hasProjectSettingsPermission={hasProjectSettingsPermission}
        inspectionStencils={inspectionStencils}
      />
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchStencils: (projectId, calendarName) =>
    dispatch(
      fetchInspectionCalendarStencils({
        calendarName,
        projectId,
      }),
    ),
});

const mapStateToProps = (state, ownProps) => {
  const { calendar } = state;
  return {
    inspectionStencils: calendar.formStencils[ownProps.calendar.name],
    projectId: APP.projectId,
  };
};

const FormStencil = PropTypes.shape({
  id: PropTypes.number,
  formSubmissionSlug: PropTypes.string,
  formTemplateSlug: PropTypes.string,
});

FixedFrequencyInspectionCalendarCard.propTypes = {
  calendar: FormCalendar.isRequired,
  fetchStencils: PropTypes.func.isRequired,
  inspectionStencils: PropTypes.arrayOf(FormStencil),
  organizationId: PropTypes.string,
  hasProjectSettingsPermission: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
};

FixedFrequencyInspectionCalendarCard.defaultProps = {
  hasProjectSettingsPermission: false,
  inspectionStencils: [],
  organizationId: null,
};

// eslint-disable-next-line import/no-default-export
export default withProvider(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withPermissions(FixedFrequencyInspectionCalendarCard)),
);
